/**
 * useAgentPort — discover the local Print Agent's port across the candidate list.
 *
 * BLU-410: previously the frontend hardcoded `http://localhost:9876`. When VS
 * Code (or another process) squatted 9876 on the operator's PC, the agent
 * crashed silently and the frontend talked to the squatter, looking like the
 * agent was offline. This hook probes the candidate ports for THIS web app's
 * flavour (see agentFlavour.ts) and remembers the last good one.
 *
 * Quiet by design: every probe of a closed port is a red
 * ERR_CONNECTION_REFUSED line in the browser console, so discovery
 *  - tries the last good port first,
 *  - then probes the candidates ONE AT A TIME and stops at the first agent
 *    of the right flavour,
 *  - is shared by every hook instance on the page (one scan, not one each),
 *  - and, when nothing answers, rescans with backoff (2 s, 5 s, 15 s, then
 *    every 30 s) instead of hammering.
 *
 * Manual override: if discovery finds nothing, falls back to
 * localStorage[printAgent.manualPort] — settable via the input in
 * PrintAgentSection.
 *
 * Uses 127.0.0.1 (not localhost) to skip the IPv4/IPv6 resolution lottery
 * that also bit BLU-410.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  candidatePortsFor,
  classifyAgent,
  expectedAgentFlavour,
  logIgnoredAgent,
  parseAgentFlavour,
  type AgentFlavour,
  type AgentIdentity,
} from './agentFlavour';

// localStorage is per origin, so the staging and prod sites each keep their
// own last good port.
const LAST_GOOD_KEY = 'printAgent.port.v1';
const MANUAL_OVERRIDE_KEY = 'printAgent.manualPort';
const PROBE_TIMEOUT_MS = 1000;
/** Rescan delays while no agent is found; the last one repeats. */
export const RESCAN_BACKOFF_MS = [2_000, 5_000, 15_000, 30_000] as const;

export type AgentPortStatus = 'discovering' | 'connected' | 'not_found';

interface UseAgentPortResult {
  port: number | null;
  status: AgentPortStatus;
  /**
   * An agent of the OTHER flavour that this site skipped (e.g. the prod agent
   * seen from the staging site), for the settings notice. Null when none.
   */
  ignoredAgent: AgentIdentity | null;
  retry: () => void;
}

interface ProbeResponse {
  ok?: unknown;
  port?: unknown;
  capabilities?: unknown;
  backendUrl?: unknown;
  flavour?: unknown;
}

/**
 * BLU-429: `backendUrl` is the agent's configured BACKEND_URL. `flavour`
 * ('prod' | 'staging', agent 2.0.22+) says which desktop app it is; older
 * agents leave it undefined.
 */
type ProbeHit = AgentIdentity;

/**
 * Probe a single port. Returns the agent's metadata on success, null on any
 * failure / timeout / wrong shape. The shape check guards against an unrelated
 * service on a candidate port returning {ok:true}.
 */
async function probePort(port: number): Promise<ProbeHit | null> {
  try {
    const res = await fetch(`http://127.0.0.1:${port}/status`, {
      signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
    });
    if (!res.ok) return null;
    const body = (await res.json()) as ProbeResponse;
    if (body.ok !== true) return null;
    if (typeof body.port !== 'number') return null;
    if (!Array.isArray(body.capabilities)) return null;
    if (body.port !== port) return null;
    return {
      port,
      backendUrl: typeof body.backendUrl === 'string' ? body.backendUrl : undefined,
      flavour: parseAgentFlavour(body.flavour),
    };
  } catch {
    return null;
  }
}

/**
 * BLU-429: the IMS frontend's own backend URL. Vite bakes
 * `import.meta.env.VITE_BACKEND_URL` at build time. Undefined in dev builds
 * with no .env override.
 */
function getExpectedBackendUrl(): string | undefined {
  const url = import.meta.env.VITE_BACKEND_URL;
  return typeof url === 'string' && url.length > 0 ? url : undefined;
}

interface ExpectedAgent {
  flavour: AgentFlavour | undefined;
  backendUrl: string | undefined;
}

interface Discovery {
  port: number | null;
  ignored: AgentIdentity | null;
}

/**
 * Find the right agent for THIS web app.
 *
 * 1. The last good port (one probe in the common case).
 * 2. The candidates in order, one at a time. An agent of the right flavour
 *    (or, for older agents that don't report one, a backendUrl match) wins
 *    at once and the scan stops. An older agent that matches neither is
 *    kept as a fallback, as before. An agent of the other flavour is skipped.
 * 3. The operator's manual port.
 */
async function discover(expected: ExpectedAgent): Promise<Discovery> {
  let ignored: AgentIdentity | null = null;
  let fallback: number | null = null;

  // Returns true when discovery can stop at this hit.
  const consider = (hit: ProbeHit | null): boolean => {
    if (hit === null) return false;
    const fit = classifyAgent(hit, expected);
    if (fit === 'reject') {
      ignored ??= hit;
      logIgnoredAgent(hit, expected.flavour);
      return false;
    }
    if (fit === 'match') return true;
    fallback ??= hit.port;
    return false;
  };

  const lastGood = readLastGood();
  if (lastGood !== null) {
    const hit = await probePort(lastGood);
    // An older agent on the last good port is still the one we used last
    // time; keep it unless its backendUrl now says another environment.
    const envMismatch =
      hit !== null &&
      hit.flavour === undefined &&
      expected.backendUrl !== undefined &&
      hit.backendUrl !== undefined &&
      hit.backendUrl !== expected.backendUrl;
    if (hit !== null && !envMismatch && classifyAgent(hit, expected) !== 'reject') {
      return { port: hit.port, ignored: null };
    }
    consider(hit);
  }

  for (const port of candidatePortsFor(expected.flavour)) {
    if (port === lastGood) continue;
    const hit = await probePort(port);
    if (consider(hit)) return { port, ignored };
  }
  if (fallback !== null) return { port: fallback, ignored };

  const manual = readManualOverride();
  if (manual !== null) {
    // Manual override is operator-chosen — trust their pick even on a
    // backendUrl mismatch (cross-env debugging). An agent that REPORTS the
    // other flavour is still refused.
    const hit = await probePort(manual);
    if (hit !== null && classifyAgent(hit, expected) !== 'reject') {
      return { port: hit.port, ignored };
    }
    consider(hit);
  }
  return { port: null, ignored };
}

// Every hook instance on the page shares one scan at a time.
let inflight: Promise<Discovery> | null = null;
// The port the last finished scan found, for the whole page. A hook that
// mounts per order (the packer's ShippingPanel) starts from it instead of
// from null, so a label can print before that hook's own re-check finishes.
let pagePort: number | null = null;

function discoverShared(expected: ExpectedAgent): Promise<Discovery> {
  inflight ??= discover(expected)
    .then((found) => {
      pagePort = found.port;
      return found;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/** How long a print waits for a running scan before giving up. */
const RESOLVE_WAIT_MS = 10_000;

/**
 * The agent's port now, or once the scan in flight ends. Lets a print fired
 * the moment a screen mounts (packer auto-print) wait for discovery instead
 * of reporting "agent not reachable". Null when no agent is found in time.
 */
export async function resolveAgentPort(timeoutMs = RESOLVE_WAIT_MS): Promise<number | null> {
  if (pagePort !== null || inflight === null) return pagePort;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), timeoutMs);
  });
  try {
    return await Promise.race([inflight.then((found) => found.port), timeout]);
  } finally {
    clearTimeout(timer);
  }
}

/** Tests only: forget the page's shared scan state. */
export function resetAgentPortForTests(): void {
  inflight = null;
  pagePort = null;
}

function readManualOverride(): number | null {
  try {
    const raw = window.localStorage.getItem(MANUAL_OVERRIDE_KEY);
    if (!raw) return null;
    const parsed = Number.parseInt(raw, 10);
    if (!Number.isInteger(parsed) || parsed < 49152 || parsed > 65535) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Read the operator's manual port override from localStorage.
 * Returns null when unset or out of the IANA dynamic/private range
 * (49152..65535) — same range the UI input enforces.
 */
export function readManualOverridePort(): number | null {
  return readManualOverride();
}

/**
 * Write the operator's manual port override. Pass null to clear.
 * Validation matches readManualOverridePort: only integers in
 * 49152..65535 are persisted; anything else is treated as a clear.
 */
export function writeManualOverridePort(port: number | null): void {
  try {
    if (port === null || !Number.isInteger(port) || port < 49152 || port > 65535) {
      window.localStorage.removeItem(MANUAL_OVERRIDE_KEY);
      return;
    }
    window.localStorage.setItem(MANUAL_OVERRIDE_KEY, String(port));
  } catch {
    /* localStorage unavailable (private mode) — silently no-op */
  }
}

function readLastGood(): number | null {
  try {
    const raw = window.localStorage.getItem(LAST_GOOD_KEY);
    if (!raw) return null;
    const parsed = Number.parseInt(raw, 10);
    if (!Number.isInteger(parsed) || parsed < 1 || parsed > 65535) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeLastGood(port: number): void {
  try {
    window.localStorage.setItem(LAST_GOOD_KEY, String(port));
  } catch {
    // localStorage may be unavailable in some browsers / private modes.
  }
}

export function useAgentPort(): UseAgentPortResult {
  // Start from the page's last good port; the scan below re-checks it.
  const [port, setPort] = useState<number | null>(() => pagePort);
  const [status, setStatus] = useState<AgentPortStatus>(() =>
    pagePort !== null ? 'connected' : 'discovering',
  );
  const [ignoredAgent, setIgnoredAgent] = useState<AgentIdentity | null>(null);
  // Bumped by retry() (operator action) to restart discovery from scratch.
  const [trigger, setTrigger] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;

    const expectedBackendUrl = getExpectedBackendUrl();
    const expected: ExpectedAgent = {
      flavour: expectedAgentFlavour(expectedBackendUrl),
      backendUrl: expectedBackendUrl,
    };

    const run = async () => {
      const found = await discoverShared(expected);
      if (cancelled) return;
      setIgnoredAgent(found.ignored);
      if (found.port !== null) {
        writeLastGood(found.port);
        setPort(found.port);
        setStatus('connected');
        return;
      }
      setPort(null);
      setStatus('not_found');
      // Nothing answered: look again later, backing off.
      const delay = RESCAN_BACKOFF_MS[Math.min(attempt, RESCAN_BACKOFF_MS.length - 1)]!;
      attempt += 1;
      timer = setTimeout(() => void run(), delay);
    };

    // A known page port stays usable while it is re-checked.
    if (pagePort === null) setStatus('discovering');
    void run();

    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
    };
  }, [trigger]);

  const retry = useCallback(() => {
    // Operator asked for a fresh scan (e.g. after setting a custom port).
    pagePort = null;
    setTrigger((n) => n + 1);
  }, []);

  return { port, status, ignoredAgent, retry };
}

/** This web app's preferred agent port: the manual-override UI's placeholder. */
export function defaultAgentPort(): number {
  return candidatePortsFor(expectedAgentFlavour(getExpectedBackendUrl()))[0]!;
}
